import { Injector } from '@angular/core';
import {
  ConnectedPosition,
  FlexibleConnectedPositionStrategy,
  OverlayRef,
  createFlexibleConnectedPositionStrategy,
  createOverlayRef,
  createRepositionScrollStrategy,
} from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';

const OFFSET = 4;
/** Start-aligned, else end-aligned; the side is chosen once on open (`SbDateTimeOverlay.place`). */
const BELOW: ConnectedPosition[] = [
  { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: OFFSET },
  { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: OFFSET },
];
const ABOVE: ConnectedPosition[] = [
  { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -OFFSET },
  { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -OFFSET },
];
/** The least height a capped panel keeps, a few rows, when neither side has room. */
const MIN_HEIGHT = 160;
/** A tap this soon after the tap that opened the panel is the second of a double-tap (Android's limit is 300ms). */
const DOUBLE_TAP_MS = 500;
/** How far from the first the second click of a double-click, or tap of a double-tap, lands: half a fingertip's 48px. */
const DOUBLE_TAP_SLOP = 24;

const TABBABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Touch first: taps on the input type, the trigger opens the panel, focus goes back to the trigger. */
export const coarsePointer = (): boolean => typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches;

/** Below a 500px viewport the panel stacks. */
export const narrowViewport = (): boolean => typeof matchMedia === 'function' && matchMedia('(max-width: 499.98px)').matches;

/** The space the panel keeps clear: the navbar, the page footer as tall as it is, and a visible sticky save bar. */
export const overlayMargins = (): { top: number; bottom: number; start: number; end: number } => {
  // The layout variables are calc()s; a probe element resolves them.
  const px = (name: string) => {
    const probe = document.createElement('div');
    probe.style.cssText = `position:absolute;visibility:hidden;height:var(${name}, 0px)`;
    document.body.appendChild(probe);
    const height = probe.offsetHeight;
    probe.remove();
    return height;
  };
  // The page footer is fixed to the bottom; on a phone its links wrap to two lines, taller than the
  // reserve the layout keeps for one.
  const footer = Array.from(document.querySelectorAll<HTMLElement>('.footer'))
    .filter((el) => getComputedStyle(el).position === 'fixed')
    .map((el) => el.getBoundingClientRect())
    .filter((rect) => rect.height > 0 && rect.bottom >= innerHeight - 2)
    .reduce((height, rect) => Math.max(height, innerHeight - rect.top), 0);
  const reserve = Math.max(px('--sn-footer-reserve'), footer);
  // Only a save bar stuck to the bottom right now (on the footer reserve's edge) takes room from the panel.
  const bottom = Array.from(document.querySelectorAll<HTMLElement>('.sb-sticky-actions, .sb-form--sticky-footer .sb-form__footer'))
    .map((bar) => bar.getBoundingClientRect())
    .filter((rect) => rect.height > 0 && rect.top < innerHeight && rect.bottom >= innerHeight - reserve - 2)
    .reduce((edge, rect) => Math.min(edge, rect.top), innerHeight - reserve);
  return { top: px('--sn-navbar-height') + 8, bottom: innerHeight - bottom + 8, start: 8, end: 8 };
};

/**
 * Lays the panel out for the room the screen leaves it, before it is placed. The quick picks are a
 * column left of the calendar, and a datetime's time columns sit beside the calendar, else under
 * it. Where the column does not fit beside the calendar, and always on a phone, the picks wrap
 * above it (`--stacked`); a panel whose stacked picks need more than three rows widens, 40px at a
 * time, up to 720px (a datetime panel 760px) and never past the screen.
 */
export const fitPanel = (pane: HTMLElement): void => {
  const panel = pane.querySelector<HTMLElement>('.sb-dt-panel');
  const body = panel?.querySelector<HTMLElement>('.sb-dt-panel__body');
  if (!panel || !body) {
    return;
  }
  const picks = Array.from(panel.querySelectorAll<HTMLElement>('.sb-dt-pick'));
  const datetime = panel.classList.contains('sb-dt-panel--datetime');
  // Fitted again as the window is resized: the width below is this layout's, not the next.
  panel.style.width = '';
  const lay = (stacked: boolean, below: boolean) => {
    panel.classList.toggle('sb-dt-panel--stacked', stacked);
    panel.classList.toggle('sb-dt-panel--time-below', below);
    return body.scrollWidth <= body.clientWidth;
  };
  if (panel.classList.contains('sb-dt-panel--narrow')) {
    lay(picks.length > 0, datetime);
  } else {
    const layouts = (picks.length ? [false, true] : [false])
      .flatMap((stacked) => (datetime ? [false, true] : [false]).map((below) => [stacked, below]));
    if (!layouts.some(([stacked, below]) => lay(stacked, below))) {
      lay(picks.length > 0, datetime);
    }
  }
  if (!panel.classList.contains('sb-dt-panel--stacked')) {
    return;
  }
  const cap = Math.min(document.documentElement.clientWidth - 16, datetime ? 760 : 720);
  const rows = () => new Set(picks.map((pick) => pick.offsetTop)).size;
  // A panel held narrower (the design-system sample in its showcase) stops growing.
  for (let width = panel.offsetWidth; rows() > 3 && width < cap; ) {
    panel.style.width = `${Math.min(cap, width + 40)}px`;
    if (panel.offsetWidth <= width) {
      break;
    }
    width = panel.offsetWidth;
  }
};

/**
 * A press or click after the first of a double-click (its `detail` counts the clicks): on the
 * button that opened the panel it neither closes the panel again nor, inside the panel, picks.
 */
export const isRepeatClick = (event: MouseEvent): boolean => event.detail > 1;

/**
 * Drops the rest of a double-click, or a double-tap, whose first click was at `at`: a press made a
 * fingertip's width from that spot within DOUBLE_TAP_MS of the first click, on whatever the panel
 * was over there (a submit button, a disabled control that would give focus to the page), goes no
 * further than the window, nor does the rest of it: its cancelled pointerdown moves no focus, its
 * mousedown, click and dblclick reach nothing. A press is judged by when it was made, not when it
 * is handled, so a pick that starts heavy work on the page (reloading a list) changes nothing, and
 * every other press is the browser's own, all of it. The first press made too late to count ends
 * the listening.
 */
const dropSecondClick = (at: { x: number; y: number; time: number }): (() => void) => {
  const events = ['pointerdown', 'pointerup', 'pointercancel', 'mousedown', 'mouseup', 'click', 'dblclick'] as const;
  let dropping = false;
  const drop = (event: Event) => {
    if (event.type === 'pointerdown') {
      if (event.timeStamp - at.time >= DOUBLE_TAP_MS) {
        stop();
        return;
      }
      const { button, clientX, clientY } = event as PointerEvent;
      dropping = button === 0 && Math.abs(clientX - at.x) <= DOUBLE_TAP_SLOP && Math.abs(clientY - at.y) <= DOUBLE_TAP_SLOP;
    }
    // A click from the keyboard (Enter on a button) belongs to no press.
    if (dropping && (event.type !== 'click' || (event as MouseEvent).detail > 0)) {
      event.preventDefault();
      event.stopPropagation();
    }
  };
  const stop = () => events.forEach((type) => window.removeEventListener(type, drop, true));
  events.forEach((type) => window.addEventListener(type, drop, true));
  return stop;
};

/**
 * A press on a dialog's backdrop with the panel open closes the panel alone, as Esc does: the click
 * it ends in, which closes the dialog, goes no further than the dialog's window. The next press is
 * the dialog's again, also when this one ended in no click (on its scrollbar).
 */
const keepDialogOpen = (dialog: EventTarget): void => {
  const drop = (event: Event) => {
    if (event.target === dialog) {
      event.stopPropagation();
    }
    stop();
  };
  const stop = () => {
    dialog.removeEventListener('click', drop, true);
    window.removeEventListener('pointerdown', stop, true);
  };
  dialog.addEventListener('click', drop, true);
  window.addEventListener('pointerdown', stop, true);
};

/** What scrolls `el`: its nearest scrolling ancestor (an ng-bootstrap modal's `.modal`), else the page. */
const scrollerOf = (el: HTMLElement): Element => {
  for (let node = el.parentElement; node && node !== document.body; node = node.parentElement) {
    if (/auto|scroll/.test(getComputedStyle(node).overflowY) && node.scrollHeight > node.clientHeight) {
      return node;
    }
  }
  return document.scrollingElement ?? document.documentElement;
};

/** Focuses the tabbable element after `from` in the page, skipping the panel. */
export const focusAfter = (from: HTMLElement, skip: HTMLElement | null): void => {
  const all = Array.from(document.querySelectorAll<HTMLElement>(TABBABLE))
    .filter((el) => (!skip || !skip.contains(el)) && el.offsetParent !== null);
  const next = all[all.indexOf(from) + 1];
  (next ?? from).focus();
};

/**
 * The popover's CDK overlay: below or above the field (start- or end-aligned, 4px away), sized to
 * the room it has, kept off the navbar, the footer and a sticky save bar. A pointer press or focus
 * landing outside it is reported to the field, a press on a dialog's backdrop closes it and not the
 * dialog, and a scroll outside it that moves the field closes it (an ng-bootstrap modal scrolls its
 * own `.modal` element, which the reposition strategy never hears about).
 */
export class SbDateTimeOverlay {
  private ref: OverlayRef | null = null;
  private teardown: (() => void)[] = [];
  /** Where and when a click inside the panel was, while it is handled (until the next task): a close then is that click's. */
  private clickInside: { x: number; y: number; time: number } | null = null;
  private stopDropping: (() => void) | null = null;

  constructor(private readonly injector: Injector) {}

  get element(): HTMLElement | null {
    return this.ref?.overlayElement ?? null;
  }

  contains(node: Node | null): boolean {
    return !!node && !!this.ref?.overlayElement.contains(node);
  }

  /**
   * `label` is a label above the origin that a panel above it keeps clear of (a range's group
   * label); a single field's floated label sits in its control's top margin and needs none.
   */
  open(origin: HTMLElement, portal: TemplatePortal, onOutside: (target: Node) => void, onScroll: () => void, onResize: (narrow: boolean) => void, label: HTMLElement | null = null): void {
    const margins = overlayMargins();
    const strategy = createFlexibleConnectedPositionStrategy(this.injector, origin)
      .withPositions(BELOW)
      .withFlexibleDimensions(false)
      // Push only ever moves the panel sideways: it is capped to the height of its side. The
      // vertical margins are applied here, not by the CDK, which measures the bottom edge as
      // the viewport's height less both margins and so pushed panels up over their field.
      .withPush(true)
      .withViewportMargin({ top: 0, bottom: 0, start: margins.start, end: margins.end });
    this.ref = createOverlayRef(this.injector, {
      positionStrategy: strategy,
      scrollStrategy: createRepositionScrollStrategy(this.injector),
      hasBackdrop: false,
      panelClass: 'sb-dt-pane',
      maxWidth: 'calc(100vw - 16px)',
    });
    this.ref.attach(portal);
    fitPanel(this.ref.overlayElement);
    this.place(origin, strategy, margins, label);
    // The room the window leaves changes as it is resized (or zoomed): the layout and the cap follow it.
    const resized = () => {
      const pane = (this.ref as OverlayRef).overlayElement;
      const narrow = narrowViewport();
      onResize(narrow);
      pane.querySelector('.sb-dt-panel')?.classList.toggle('sb-dt-panel--narrow', narrow);
      fitPanel(pane);
      this.place(origin, strategy, overlayMargins(), label, false);
      if (this.contains(document.activeElement)) {
        document.activeElement?.scrollIntoView({ block: 'nearest' });
      }
    };
    window.addEventListener('resize', resized);
    // A click is judged by where its press began, as the CDK does: a press on the field that opened
    // the panel ends in a click on a common ancestor once the field has redrawn under the pointer.
    // A click whose press came before the panel was there belongs to the gesture that opened it.
    let pressed: Node | null | undefined;
    let touch = false;
    const press = (event: PointerEvent) => {
      pressed = event.target as Node;
      touch = event.pointerType === 'touch';
      if ((event.target as Element).matches('.modal')) {
        keepDialogOpen(event.target as Element);
      }
    };
    const outside = this.ref.outsidePointerEvents().subscribe((event) => {
      if (event.type === 'click' && pressed === undefined) {
        return;
      }
      onOutside((event.type === 'click' && pressed) || (event.target as Node));
    });
    document.addEventListener('pointerdown', press, true);
    // A scroll event can arrive just after the panel opened, from a scroll that brought the field
    // into view before it; only a scroll that moves the field from where the panel opened counts.
    // Focus moving inside the panel scrolls the page as far as the focused row needs, a pixel at a
    // column's edge: the scroll events of that frame move the anchor with the field instead.
    let anchor = origin.getBoundingClientRect();
    let refocused = false;
    const focused = (event: FocusEvent) => {
      if (!this.contains(event.target as Node)) {
        onOutside(event.target as Node);
        return;
      }
      refocused = true;
      requestAnimationFrame(() => (refocused = false));
    };
    const scrolled = (event: Event) => {
      const field = origin.getBoundingClientRect();
      if (this.contains(event.target as Node) || (Math.abs(field.top - anchor.top) <= 1 && Math.abs(field.left - anchor.left) <= 1)) {
        return;
      }
      if (refocused) {
        anchor = field;
        return;
      }
      onScroll();
    };
    document.addEventListener('focusin', focused, true);
    document.addEventListener('scroll', scrolled, true);
    // The second click of a double-click on the button (or the field's blank end) that opened the
    // panel lands on whatever the panel, or the page scrolled to fit it, put under the pointer: a
    // pick, a day, an hour, the button again, or the page beside a panel narrower than its field.
    // It belongs to the gesture that opened the panel and is dropped wherever it lands, press and
    // click: it picks nothing, closes nothing and moves no focus out of the panel. On a touch screen
    // Chrome does not always count the second tap of a double-tap as one: the first tap that comes
    // that soon is dropped too. Once a press has begun anew, every click is the user's.
    let opening = true;
    let firstPress = true;
    let tapped = false;
    const openedAt = performance.now();
    const repeated = (event: MouseEvent) => {
      if (event.type === 'mousedown') {
        tapped = firstPress && touch && performance.now() - openedAt < DOUBLE_TAP_MS;
        firstPress = false;
      }
      if (opening && (isRepeatClick(event) || tapped)) {
        event.preventDefault();
        event.stopPropagation();
      } else if (event.type === 'mousedown') {
        opening = false;
      }
    };
    document.addEventListener('mousedown', repeated, true);
    document.addEventListener('click', repeated, true);
    // A click from the keyboard (Enter on a pick) has no second click to drop.
    const clicked = (event: MouseEvent) => {
      if (this.contains(event.target as Node) && event.detail > 0) {
        this.clickInside = { x: event.clientX, y: event.clientY, time: event.timeStamp };
        setTimeout(() => (this.clickInside = null));
      }
    };
    document.addEventListener('click', clicked, true);
    this.teardown = [
      () => window.removeEventListener('resize', resized),
      () => document.removeEventListener('click', clicked, true),
      () => document.removeEventListener('mousedown', repeated, true),
      () => document.removeEventListener('click', repeated, true),
      () => outside.unsubscribe(),
      () => document.removeEventListener('pointerdown', press, true),
      () => document.removeEventListener('focusin', focused, true),
      () => document.removeEventListener('scroll', scrolled, true),
    ];
  }

  /**
   * Below the field when the panel fits there, else above when it fits there. When neither side
   * has room the page first scrolls the field up, no further than the navbar, so the panel fits
   * under it and its calendar is never cut; if the screen is too short even then, the panel takes
   * the roomier side, capped to it with its body scrolling. A panel that fits as it is opens as it
   * is; a month with another week that needs more room then caps it (`measureCapped`). Never over
   * the navbar, the footer or a sticky save bar, and, for a field the window no longer shows or too
   * short a screen for the least height, moved back inside the window over the field. The CDK's own
   * flexible sizing mixes up the top and bottom margins, so it is not used. Placed again as the
   * window is resized, the page is not scrolled: that would close the panel.
   */
  private place(origin: HTMLElement, strategy: FlexibleConnectedPositionStrategy, margins: ReturnType<typeof overlayMargins>, label: HTMLElement | null, scroll = true): void {
    const ref = this.ref as OverlayRef;
    // A floated label sits in the control's top margin, a range's group label above its inputs; a
    // panel above the field stays clear of either.
    const lift = label
      ? Math.max(0, origin.getBoundingClientRect().top - label.getBoundingClientRect().top)
      : parseFloat(getComputedStyle(origin).marginTop) || 0;
    // The CDK places a panel above its field by its distance from the bottom of the document's
    // client area, an end-aligned one by its distance from the right, and pushes a panel below its
    // field back inside that area. On a phone page wider than the screen the overlay's box is
    // larger than that area: the panel would land lower (over the field) and further right by the
    // difference, and a panel below that reached past the area's bottom would be pushed up over it.
    const box = ref.hostElement.parentElement?.getBoundingClientRect();
    const tall = box ? Math.max(0, Math.round(box.height - document.documentElement.clientHeight)) : 0;
    const wide = box ? Math.max(0, Math.round(box.width - document.documentElement.clientWidth)) : 0;
    const room = () => {
      const field = origin.getBoundingClientRect();
      return { below: innerHeight - tall - margins.bottom - field.bottom - OFFSET, above: field.top - lift - margins.top - OFFSET };
    };
    // Its own height, not the cap of the last place.
    ref.updateSize({ maxHeight: undefined });
    const height = ref.overlayElement.getBoundingClientRect().height;
    let { below, above } = room();
    if (scroll && height > below && height > above && above > 0) {
      scrollerOf(origin).scrollBy({ top: Math.min(height - below, above), behavior: 'instant' });
      ({ below, above } = room());
    }
    const useBelow = height <= below || (height > above && below >= above);
    // Start-aligned when the panel fits from the field's start, else end-aligned when it fits back
    // from its end, else 8px from the screen's start edge. The CDK measures its start margin from 0,
    // so a panel 2px from the edge counted as fitting there.
    const field = origin.getBoundingClientRect();
    const width = ref.overlayElement.getBoundingClientRect().width;
    const viewport = document.documentElement.clientWidth;
    const align = field.left + width <= viewport - margins.end ? 'start' : field.right - width >= margins.start ? 'end' : 'edge';
    // Whole pixels: a fractional cap that exactly fills the side reads as overflowing, and the
    // CDK would push the panel over its field.
    const bottom = innerHeight - tall - margins.bottom;
    const cap = Math.min(Math.max(Math.floor(useBelow ? below : above), MIN_HEIGHT), Math.floor(bottom - margins.top));
    const shown = Math.min(height, cap);
    const top = useBelow ? field.bottom + OFFSET : field.top - lift - OFFSET - shown;
    const inside = Math.max(margins.top, Math.min(top, bottom - shown)) - top;
    // Pushed onto the screen, the CDK moves the panel without its offset and adds the offset after:
    // a panel as wide as the screen, placed 8px from its edge by the offset, is not pushed too.
    strategy.withPush(align !== 'edge');
    strategy.withPositions((useBelow ? BELOW : ABOVE).filter((position) => position.overlayX === (align === 'end' ? 'end' : 'start')).map((position) => ({
      ...position,
      offsetX: align === 'end' ? -wide : align === 'edge' ? margins.start - field.left : 0,
      offsetY: (useBelow ? OFFSET : -OFFSET - lift - tall) + inside,
    })));
    ref.updateSize({ maxHeight: cap });
    ref.updatePosition();
  }

  /**
   * A pick or a day that closes the panel leaves the page under the pointer: the second click of a
   * double-click on it, or of a double-tap, would land on whatever the panel was over there (a table
   * cell, another field) and is dropped, press, click and dblclick.
   */
  close(): void {
    const closedBy = this.ref ? this.clickInside : null;
    this.teardown.forEach((fn) => fn());
    this.teardown = [];
    this.clickInside = null;
    this.ref?.dispose();
    this.ref = null;
    if (closedBy) {
      this.stopDropping?.();
      this.stopDropping = dropSecondClick(closedBy);
    }
  }

  /** Closes the panel for good, with its field: the rest of a double-click no longer has a page to land on. */
  destroy(): void {
    this.close();
    this.stopDropping?.();
    this.stopDropping = null;
  }
}
